import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, ImagePlus, Loader2, ShieldAlert, Trash2, Upload } from "lucide-react";
import { LANDING_PHOTOS_MAX, type SiteMedia } from "@wedding/shared";
import { adminSiteApi, uploadSiteMedia, type SiteMediaKind } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * "Satış & Site → Vitrin": landing'deki stok görsellerin yerine gerçek düğün
 * kareleri + Sahne bölümüne gerçek ekran videosu.
 *
 * Yükleme = anında kayıt (ayrı "Kaydet" yok): worker R2'ye yazar ve `site:media`yı
 * aynı istekte günceller. Sıralama tek tıkla kaydedilir. Her eylem toast'lı.
 */

const ERROR_CODES = ["too_large", "mp4_only", "image_only", "limit", "network", "media_changed", "length_required"] as const;

const cdn = (key: string, w?: number) => `/cdn/${key}${w ? `?w=${w}` : ""}`;

type Props = {
  media: SiteMedia;
  onChange: (media: SiteMedia) => void;
  showToast: (msg: string, ok?: boolean) => void;
  /** Vitrin başka sekmede değiştiyse (409 media_changed) sunucudan yeniden yükle. */
  reload?: () => void;
};

/** Son yüklemenin KALICI sonucu — toast 3 sn'de kaybolur, bu kalır (2026-10-16: "tamamlandı ama kayboldu"). */
type Outcome = { kind: SiteMediaKind; ok: boolean; text: string };

export function MediaTab({ media, onChange, showToast, reload }: Props) {
  const { t } = useTranslation();
  // Yükleme sürerken hangi alan meşgul + ilerleme
  const [busy, setBusy] = useState<{ kind: SiteMediaKind; pct: number; label?: string } | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const errorText = (err: unknown) => {
    const code = err instanceof Error ? err.message : String(err);
    return (ERROR_CODES as readonly string[]).includes(code)
      ? t(`siteAdmin.media.err.${code}`)
      : t("siteAdmin.saveFailed", { error: code });
  };

  const upload = async (kind: SiteMediaKind, files: File[]) => {
    let latest = media;
    let done = 0;
    setOutcome(null);
    for (const [i, file] of files.entries()) {
      const label = files.length > 1 ? t("siteAdmin.media.uploadingN", { n: i + 1, total: files.length }) : undefined;
      setBusy({ kind, pct: 0, label });
      try {
        latest = await uploadSiteMedia(kind, file, (pct) => setBusy({ kind, pct, label }));
        onChange(latest);
        done++;
      } catch (err) {
        const text = `${file.name}: ${errorText(err)}`;
        setOutcome({ kind, ok: false, text });
        showToast(text, false);
        break;
      }
    }
    setBusy(null);
    if (done > 0) {
      const text = t("siteAdmin.media.uploaded", { count: done });
      setOutcome((o) => (o && !o.ok ? o : { kind, ok: true, text }));
      showToast(text);
    }
  };

  const remove = async (kind: SiteMediaKind, key?: string) => {
    if (!window.confirm(t("siteAdmin.media.removeConfirm"))) return;
    setBusy({ kind, pct: 0 });
    try {
      const res = await adminSiteApi.deleteMedia(kind, key);
      if (res.data) onChange(res.data);
      showToast(t("siteAdmin.media.removed"));
    } catch (err) {
      showToast(errorText(err), false);
    } finally {
      setBusy(null);
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const photos = [...media.photos];
    const [k] = photos.splice(index, 1);
    photos.splice(index + dir, 0, k);
    const next = { ...media, photos };
    setBusy({ kind: "photo", pct: 0 });
    try {
      const res = await adminSiteApi.put("media", next);
      onChange(res.data ?? next);
      showToast(t("siteAdmin.saved"));
    } catch (err) {
      showToast(errorText(err), false);
      if (err instanceof Error && err.message === "media_changed") reload?.();
    } finally {
      setBusy(null);
    }
  };

  /** Yükleme durumu: bayt gönderimi %100 olunca sunucu kaydı bitene kadar "işleniyor" — "tamamlandı" sanılmasın. */
  const status = (kind: SiteMediaKind) => (
    <>
      {busy?.kind === kind && (busy.pct > 0 || kind === "video") && (
        <Progress pct={busy.pct} label={busy.pct >= 100 ? t("siteAdmin.media.processing") : busy.label} />
      )}
      {!busy && outcome?.kind === kind && (
        <p
          role={outcome.ok ? "status" : "alert"}
          data-testid={`media-outcome-${kind}`}
          className={cn(
            "mx-5 my-2 flex items-start gap-2 rounded-xl px-3 py-2 text-xs",
            outcome.ok ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-red-500/10 text-red-700 dark:text-red-300",
          )}
        >
          {outcome.ok ? <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" /> : <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />}
          <span>{outcome.text}</span>
        </p>
      )}
    </>
  );

  const full = media.photos.length >= LANDING_PHOTOS_MAX;

  return (
    <div className="space-y-6">
      <p className="flex items-start gap-2 rounded-2xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-800 dark:text-amber-300">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        {t("siteAdmin.media.consent")}
      </p>

      {/* Gerçek kareler */}
      <section className="rounded-2xl border border-border bg-card overflow-hidden" aria-labelledby="media-photos">
        <div className="px-5 py-4 border-b border-border bg-muted/30 flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <h2 id="media-photos" className="text-sm font-semibold text-foreground">
              {t("siteAdmin.media.photosTitle")}{" "}
              <span className="font-normal text-muted-foreground tabular-nums">{media.photos.length}/{LANDING_PHOTOS_MAX}</span>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.media.photosHint")}</p>
          </div>
          <FilePick
            accept="image/jpeg,image/png,image/webp,image/avif"
            multiple
            disabled={busy !== null || full}
            loading={busy?.kind === "photo"}
            label={t("siteAdmin.media.addPhotos")}
            icon={<ImagePlus className="h-4 w-4" />}
            onFiles={(files) => void upload("photo", files.slice(0, LANDING_PHOTOS_MAX - media.photos.length))}
          />
        </div>
        {status("photo")}
        {media.photos.length === 0 ? (
          <p className="px-5 py-6 text-sm text-muted-foreground">{t("siteAdmin.media.photosEmpty")}</p>
        ) : (
          <ol className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 lg:grid-cols-4">
            {media.photos.map((key, index) => (
              <li key={key} data-testid={`media-photo-${index}`} className="group relative overflow-hidden rounded-xl border border-border bg-muted">
                <img src={cdn(key, 480)} alt="" loading="lazy" className="aspect-[4/3] w-full object-cover" />
                <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white tabular-nums">{index + 1}</span>
                <div className="flex items-center justify-between gap-1 p-2">
                  <span className="flex gap-1">
                    <IconBtn label={t("siteAdmin.proof.moveUp")} disabled={busy !== null || index === 0} onClick={() => void move(index, -1)}>
                      <ArrowLeft className="h-3.5 w-3.5" />
                    </IconBtn>
                    <IconBtn label={t("siteAdmin.proof.moveDown")} disabled={busy !== null || index === media.photos.length - 1} onClick={() => void move(index, 1)}>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </IconBtn>
                  </span>
                  <IconBtn label={t("siteAdmin.proof.remove")} danger disabled={busy !== null} onClick={() => void remove("photo", key)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </IconBtn>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Sahne videosu + kapak */}
      <section className="rounded-2xl border border-border bg-card overflow-hidden" aria-labelledby="media-video">
        <div className="px-5 py-4 border-b border-border bg-muted/30">
          <h2 id="media-video" className="text-sm font-semibold text-foreground">{t("siteAdmin.media.videoTitle")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.media.videoHint")}</p>
        </div>
        <div className="grid gap-5 p-5 lg:grid-cols-2">
          <div className="space-y-3">
            <p className="text-xs font-semibold text-foreground">{t("siteAdmin.media.video")}</p>
            {media.sahneVideo ? (
              <video data-testid="media-video" src={cdn(media.sahneVideo)} poster={media.sahnePoster ? cdn(media.sahnePoster, 960) : undefined}
                controls preload="metadata" playsInline className="aspect-video w-full rounded-xl bg-black" />
            ) : (
              <div className="flex aspect-video w-full items-center justify-center rounded-xl border border-dashed border-border text-sm text-muted-foreground">
                {t("siteAdmin.media.videoEmpty")}
              </div>
            )}
            {status("video")}
            <div className="flex flex-wrap gap-2">
              <FilePick accept="video/mp4" disabled={busy !== null} loading={busy?.kind === "video"} inputLabel={t("siteAdmin.media.video")} label={media.sahneVideo ? t("siteAdmin.media.replace") : t("siteAdmin.media.upload")}
                icon={<Upload className="h-4 w-4" />} onFiles={(f) => void upload("video", f.slice(0, 1))} />
              {media.sahneVideo && (
                <DangerBtn disabled={busy !== null} onClick={() => void remove("video")}>{t("siteAdmin.proof.remove")}</DangerBtn>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-xs font-semibold text-foreground">{t("siteAdmin.media.poster")}</p>
            {media.sahnePoster ? (
              <img data-testid="media-poster" src={cdn(media.sahnePoster, 960)} alt="" className="aspect-video w-full rounded-xl object-cover bg-muted" />
            ) : (
              <div className="flex aspect-video w-full items-center justify-center rounded-xl border border-dashed border-border px-4 text-center text-sm text-muted-foreground">
                {t("siteAdmin.media.posterEmpty")}
              </div>
            )}
            {status("poster")}
            <div className="flex flex-wrap gap-2">
              <FilePick accept="image/jpeg,image/png,image/webp,image/avif" disabled={busy !== null} loading={busy?.kind === "poster"} inputLabel={t("siteAdmin.media.poster")}
                label={media.sahnePoster ? t("siteAdmin.media.replace") : t("siteAdmin.media.upload")}
                icon={<Upload className="h-4 w-4" />} onFiles={(f) => void upload("poster", f.slice(0, 1))} />
              {media.sahnePoster && (
                <DangerBtn disabled={busy !== null} onClick={() => void remove("poster")}>{t("siteAdmin.proof.remove")}</DangerBtn>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FilePick({ accept, multiple, disabled, loading, label, inputLabel, icon, onFiles }: {
  accept: string; multiple?: boolean; disabled?: boolean; loading?: boolean; label: string;
  /** Gizli dosya girişinin erişilebilir adı (aynı sayfada iki "Yükle" düğmesi var). */
  inputLabel?: string;
  icon: React.ReactNode;
  onFiles: (files: File[]) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        tabIndex={-1}
        aria-label={inputLabel ?? label}
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = "";
          if (files.length) onFiles(files);
        }}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => ref.current?.click()}
        className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-primary/5 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
        {label}
      </button>
    </>
  );
}

function Progress({ pct, label }: { pct: number; label?: string }) {
  return (
    <div className="px-5 py-2" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      {label && <p className="mb-1 text-[11px] text-muted-foreground">{label}</p>}
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function IconBtn({ label, onClick, disabled, danger, children }: {
  label: string; onClick: () => void; disabled?: boolean; danger?: boolean; children: React.ReactNode;
}) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}
      className={cn(
        "rounded-lg border bg-background/80 p-1.5 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed",
        danger ? "border-red-500/30 text-red-500 hover:bg-red-500/5" : "border-border text-muted-foreground hover:text-foreground",
      )}>
      {children}
    </button>
  );
}

function DangerBtn({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 px-4 py-2 text-sm text-red-500 hover:bg-red-500/5 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed">
      <Trash2 className="h-4 w-4" />
      {children}
    </button>
  );
}
